// src/components/HelpCenter.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Link
} from '@mui/material';
import {
  Search,
  QuestionMark,
  Book,
  VideoLibrary,
  BookmarkBorder,
  CheckCircle,
  Info,
  Email,
  LiveHelp,
  ChevronRight,
  ExpandMore,
  HelpOutline,
  AddReactionOutlined,
  Lightbulb,
  VideoCall,
  Headset,
  Assignment,
  ContactSupport
} from '@mui/icons-material';

// Custom TabPanel component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // FAQ data structure
  const faqs = [
    {
      category: 'Account Management',
      questions: [
        {
          question: 'How do I reset my password?',
          answer: 'You can reset your password by clicking on the "Forgot Password" link on the login page. You will receive an email with instructions to create a new password.'
        },
        {
          question: 'How do I update my profile information?',
          answer: 'To update your profile, go to the user menu in the top-right corner and select "My Profile". From there, click the "Edit Profile" button to make changes to your information.'
        },
        {
          question: 'Can I change my email address?',
          answer: 'Yes, you can change your email address from your profile settings. Navigate to your profile, click "Edit Profile", and update your email. Note that you will need to verify the new email address.'
        }
      ]
    },
    {
      category: 'User Management',
      questions: [
        {
          question: 'How do I invite new users?',
          answer: 'As an admin, you can invite new users by navigating to the Admin Dashboard, clicking on the "User Management" tab, and then clicking the "Invite New User" button. Fill in the required information and send the invitation.'
        },
        {
          question: 'Can I change a user\'s role?',
          answer: 'Yes, admins can change user roles. From the Admin Dashboard, find the user you want to update, click the actions menu (three dots), and select "Edit Role". Then select the new role from the dropdown menu and save your changes.'
        },
        {
          question: 'How do I deactivate a user account?',
          answer: 'To deactivate a user account, go to the Admin Dashboard, find the user, click the actions menu, and select "Delete User". Note that this action cannot be undone and will permanently remove all user data.'
        }
      ]
    },
    {
      category: 'Profile Exports',
      questions: [
        {
          question: 'What export formats are supported?',
          answer: 'The system currently supports exporting profiles in HTML and PHP formats. More formats may be added in future updates.'
        },
        {
          question: 'How do I track exports made by users?',
          answer: 'Admins can track all profile exports by accessing the Admin Dashboard and selecting the "Export Tracking" tab. This provides a complete history of all exports, including who exported what and when.'
        },
        {
          question: 'Is there a limit to how many profiles I can export?',
          answer: 'There is no strict limit on the number of exports you can create, but we do monitor usage patterns to ensure system stability. Excessive exports within a short period may trigger rate limiting.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          question: 'Which browsers are supported?',
          answer: 'Our application supports the latest versions of Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best experience.'
        },
        {
          question: 'How do I report a bug?',
          answer: 'You can report bugs by sending an email to support@example.com with details about the issue, steps to reproduce it, and screenshots if possible.'
        },
        {
          question: 'Is there an API available?',
          answer: 'Yes, we offer a RESTful API for integrations. Please contact our support team for API documentation and access credentials.'
        }
      ]
    }
  ];

  // Quick Links data
  const quickLinks = [
    { title: 'Getting Started Guide', icon: <Book fontSize="small" />, link: '/docs/getting-started' },
    { title: 'Video Tutorials', icon: <VideoLibrary fontSize="small" />, link: '/tutorials' },
    { title: 'Admin Dashboard Guide', icon: <Assignment fontSize="small" />, link: '/docs/admin-guide' },
    { title: 'User Permissions', icon: <AddReactionOutlined fontSize="small" />, link: '/docs/permissions' },
    { title: 'Contact Support', icon: <Email fontSize="small" />, link: '/support' }
  ];

  // Video tutorials data
  const videoTutorials = [
    { 
      title: 'Getting Started with ProfileManager', 
      duration: '3:45', 
      thumbnail: '/api/placeholder/320/180', 
      link: '#tutorial1'
    },
    { 
      title: 'Admin Dashboard Overview', 
      duration: '5:20', 
      thumbnail: '/api/placeholder/320/180', 
      link: '#tutorial2'
    },
    { 
      title: 'Managing User Profiles', 
      duration: '4:10', 
      thumbnail: '/api/placeholder/320/180', 
      link: '#tutorial3'
    },
    { 
      title: 'Exporting and Tracking Profiles', 
      duration: '3:30', 
      thumbnail: '/api/placeholder/320/180', 
      link: '#tutorial4'
    }
  ];

  // Filter FAQs based on search query
  const filteredFaqs = searchQuery 
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(item => 
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Hero Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            How can we help you?
          </Typography>
          <Typography variant="h6" paragraph>
            Find answers to your questions, explore tutorials, and get the most out of your experience.
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Search for help..."
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              mt: 2,
              mb: 1,
              maxWidth: 600,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ mt: 2 }}>
            <Chip 
              label="User Guide" 
              clickable 
              sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              icon={<Book sx={{ color: 'white !important' }} />} 
            />
            <Chip 
              label="Account" 
              clickable 
              sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              icon={<HelpOutline sx={{ color: 'white !important' }} />} 
            />
            <Chip 
              label="Admin" 
              clickable 
              sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              icon={<ContactSupport sx={{ color: 'white !important' }} />} 
            />
          </Box>
        </Box>
        
        {/* Decorative circles */}
        <Box sx={{ 
          position: 'absolute', 
          right: -50, 
          top: -50, 
          width: 200, 
          height: 200, 
          borderRadius: '50%', 
          background: 'rgba(255,255,255,0.1)' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          right: 50, 
          bottom: -30, 
          width: 120, 
          height: 120, 
          borderRadius: '50%', 
          background: 'rgba(255,255,255,0.05)' 
        }} />
      </Paper>
      
      {/* Quick Support Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ bgcolor: '#2563eb', mx: 'auto', mb: 2, width: 60, height: 60 }}>
                <LiveHelp sx={{ fontSize: 35 }} />
              </Avatar>
              <Typography variant="h5" component="h2" gutterBottom fontWeight="medium">
                Live Chat Support
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Chat with our support team for immediate assistance with your questions.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Headset />}
                sx={{ mt: 1 }}
              >
                Start Chat
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ bgcolor: '#047857', mx: 'auto', mb: 2, width: 60, height: 60 }}>
                <VideoCall sx={{ fontSize: 35 }} />
              </Avatar>
              <Typography variant="h5" component="h2" gutterBottom fontWeight="medium">
                Video Tutorials
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Watch step-by-step tutorials on how to use all features of our platform.
              </Typography>
              <Button 
                variant="contained" 
                color="success"
                startIcon={<VideoLibrary />}
                sx={{ mt: 1 }}
              >
                Watch Tutorials
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ bgcolor: '#7C3AED', mx: 'auto', mb: 2, width: 60, height: 60 }}>
                <Lightbulb sx={{ fontSize: 35 }} />
              </Avatar>
              <Typography variant="h5" component="h2" gutterBottom fontWeight="medium">
                Knowledge Base
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Explore our comprehensive documentation and guides for detailed information.
              </Typography>
              <Button 
                variant="contained" 
                color="secondary"
                startIcon={<Book />}
                sx={{ mt: 1 }}
              >
                View Documentation
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Tabs & Content */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: '#f8fafc'
          }}
        >
          <Tab label="Frequently Asked Questions" icon={<QuestionMark />} iconPosition="start" />
          <Tab label="Quick Links" icon={<BookmarkBorder />} iconPosition="start" />
          <Tab label="Video Tutorials" icon={<VideoLibrary />} iconPosition="start" />
        </Tabs>
        
        {/* FAQ Tab */}
        <TabPanel value={activeTab} index={0}>
          {searchQuery && filteredFaqs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Info sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6">No results found for "{searchQuery}"</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Try different keywords or browse our categories below
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </Button>
            </Box>
          ) : (
            filteredFaqs.map((category, index) => (
              <Box key={index} sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  fontWeight="medium"
                  sx={{ mb: 2, mt: index > 0 ? 4 : 0 }}
                >
                  {category.category}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {category.questions.map((faq, faqIndex) => (
                  <Accordion 
                    key={faqIndex}
                    sx={{ 
                      mb: 1,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { mt: 0 }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography fontWeight="medium">
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {faq.answer}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Was this answer helpful?
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Button 
                            size="small" 
                            startIcon={<CheckCircle fontSize="small" />} 
                            sx={{ mr: 1 }}
                          >
                            Yes
                          </Button>
                          <Button 
                            size="small" 
                            color="secondary"
                          >
                            No
                          </Button>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))
          )}
          
          {/* Contact support section */}
          <Box 
            sx={{ 
              mt: 4, 
              p: 3, 
              bgcolor: '#f8fafc', 
              borderRadius: 2,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Still need help?
            </Typography>
            <Typography color="text.secondary" paragraph>
              If you couldn't find an answer to your question, our support team is ready to assist you.
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<Email />}
            >
              Contact Support
            </Button>
          </Box>
        </TabPanel>
        
        {/* Quick Links Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" component="h2" fontWeight="medium" gutterBottom>
                Popular Resources
              </Typography>
              <Paper variant="outlined" sx={{ p: 0, borderRadius: 2 }}>
                <List>
                  {quickLinks.map((link, index) => (
                    <React.Fragment key={index}>
                      <ListItem 
                        button 
                        component={Link} 
                        href={link.link}
                        sx={{ py: 2 }}
                      >
                        <ListItemIcon sx={{ color: '#2563eb' }}>
                          {link.icon}
                        </ListItemIcon>
                        <ListItemText primary={link.title} />
                        <ChevronRight color="action" />
                      </ListItem>
                      {index < quickLinks.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
              
              <Typography variant="h5" component="h2" fontWeight="medium" gutterBottom sx={{ mt: 4 }}>
                Latest Updates
              </Typography>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <Chip label="New Feature" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Enhanced User Export Options
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We've added new export formats and customization options for profile exports.
                  </Typography>
                  <Button size="small" sx={{ mt: 1 }}>
                    Learn more
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Chip label="Improvement" color="primary" size="small" sx={{ mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Dashboard Performance Boost
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The admin dashboard now loads 50% faster and includes new filtering capabilities.
                  </Typography>
                  <Button size="small" sx={{ mt: 1 }}>
                    Learn more
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h5" component="h2" fontWeight="medium" gutterBottom>
                Getting Started
              </Typography>
              
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    1. Setting Up Your Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Complete your profile with relevant information to make it easier for others to find and connect with you.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    endIcon={<ChevronRight />}
                  >
                    View Guide
                  </Button>
                </CardContent>
              </Card>
              
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    2. Inviting Team Members
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Learn how to invite colleagues to join your organization and manage their access levels.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    endIcon={<ChevronRight />}
                  >
                    View Guide
                  </Button>
                </CardContent>
              </Card>
              
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    3. Exporting and Sharing Profiles
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Discover how to export profiles in different formats and share them with external stakeholders.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    endIcon={<ChevronRight />}
                  >
                    View Guide
                  </Button>
                </CardContent>
              </Card>
              
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    4. Administrative Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Configure your system settings, security preferences, and organization defaults.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    endIcon={<ChevronRight />}
                  >
                    View Guide
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Video Tutorials Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h5" component="h2" fontWeight="medium" gutterBottom>
            Video Tutorials
          </Typography>
          <Typography color="text.secondary" paragraph>
            Watch these tutorials to learn how to use the platform efficiently.
          </Typography>
          
          <Grid container spacing={3}>
            {videoTutorials.map((video, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  transition: 'transform 0.2s',
                  '&:hover': { 
                    transform: 'translateY(-5px)',
                    '& .MuiCardContent-root': {
                      bgcolor: '#f8fafc'
                    }
                  }
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      style={{ width: '100%', display: 'block' }} 
                    />
                    <Box sx={{ 
                      position: 'absolute', 
                      bottom: 8, 
                      right: 8, 
                      bgcolor: 'rgba(0,0,0,0.7)', 
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem'
                    }}>
                      {video.duration}
                    </Box>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Avatar sx={{ 
                        bgcolor: 'rgba(0,0,0,0.5)', 
                        width: 50, 
                        height: 50,
                        '&:hover': {
                          bgcolor: '#2563eb',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}>
                        <VideoLibrary />
                      </Avatar>
                    </Box>
                  </Box>
                  <CardContent>
                    <Typography variant="subtitle1" component="h3" fontWeight="medium">
                      {video.title}
                    </Typography>
                    <Button 
                      size="small" 
                      color="primary" 
                      component={Link} 
                      href={video.link}
                      sx={{ mt: 1 }}
                    >
                      Watch Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button variant="contained" endIcon={<ChevronRight />}>
              View All Tutorials
            </Button>
          </Box>
        </TabPanel>
      </Paper>
      
      {/* Footer section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mt: 6, 
          pt: 4, 
          pb: 4,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Need more help?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Our support team is available Monday to Friday, 9am to 5pm EST.
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            startIcon={<Email />}
            sx={{ mx: 1, mb: 1 }}
          >
            Email Support
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            startIcon={<Headset />}
            sx={{ mx: 1, mb: 1 }}
          >
            Call Support
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          © 2025 KoorAI Pro - Profile Manager. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default HelpCenter;